import { mockProjects } from '../mocks/projects';
import { mockContracts, type Contract } from '../mocks/contracts';
import { mockProjectCosts, type ProjectCost } from '../mocks/projectCosts';
import { mockTimesheets, type MonthlyTimesheet } from '../mocks/timesheets';
import { STORAGE_KEYS, readStoredJSON, writeStoredJSON } from './state/appState';

export type ProjectCurrency = 'USD' | 'EUR' | 'TRY';
export type StoredProject = (typeof mockProjects)[number] & {
  currency?: ProjectCurrency;
};

const DEMO_PROJECT_IDS = new Set(mockProjects.map((project) => project.id));
const DEMO_CONTRACT_IDS = new Set(mockContracts.map((contract) => contract.id));
const DEMO_COST_IDS = new Set(mockProjectCosts.map((cost) => cost.id));
const DEMO_TIMESHEET_IDS = new Set(mockTimesheets.map((timesheet) => timesheet.id));
const DEMO_TIMESHEET_ENTRY_IDS = new Set(
  mockTimesheets.flatMap((timesheet) => timesheet.entries.map((entry) => entry.id)),
);

function isDemoProjectId(projectId?: string | null) {
  return typeof projectId === 'string' && DEMO_PROJECT_IDS.has(projectId);
}

function isDemoContractId(contractId?: string | null) {
  return typeof contractId === 'string' && DEMO_CONTRACT_IDS.has(contractId);
}

function isDemoDerivedCostId(costId: string) {
  return costId.startsWith('ts-cost-') && DEMO_TIMESHEET_ENTRY_IDS.has(costId.slice('ts-cost-'.length));
}

function normalizeProjectCurrency(currency?: string): ProjectCurrency {
  if (currency === 'EUR' || currency === 'TRY') {
    return currency;
  }
  return 'USD';
}

export function sanitizeAdminProjects(projects: StoredProject[]) {
  return projects
    .filter((project) => !DEMO_PROJECT_IDS.has(project.id))
    .map((project) => ({
      ...project,
      currency: normalizeProjectCurrency(project.currency),
    }));
}

export function sanitizeContracts(contracts: Contract[]) {
  return contracts.filter(
    (contract) => !DEMO_CONTRACT_IDS.has(contract.id) && !isDemoProjectId(contract.projectId),
  );
}

export function sanitizeProjectCosts(costs: ProjectCost[]) {
  return costs.filter((cost) => {
    if (DEMO_COST_IDS.has(cost.id)) return false;
    if (isDemoDerivedCostId(cost.id)) return false;
    if (isDemoProjectId(cost.projectId)) return false;
    if (isDemoContractId(cost.contractId)) return false;
    if (typeof cost.timesheetEntryId === 'string' && DEMO_TIMESHEET_ENTRY_IDS.has(cost.timesheetEntryId)) {
      return false;
    }
    return true;
  });
}

export function sanitizeTimesheets(timesheets: MonthlyTimesheet[]) {
  return timesheets
    .filter((timesheet) => !DEMO_TIMESHEET_IDS.has(timesheet.id))
    .map((timesheet) => ({
      ...timesheet,
      entries: timesheet.entries.filter((entry) => !isDemoProjectId(entry.projectId)),
    }))
    .filter((timesheet) => timesheet.entries.length > 0 || !DEMO_TIMESHEET_IDS.has(timesheet.id));
}

export function loadAdminProjects() {
  return sanitizeAdminProjects(readStoredJSON<StoredProject[]>(STORAGE_KEYS.adminProjects, []));
}

export function saveAdminProjects(projects: StoredProject[]) {
  writeStoredJSON(STORAGE_KEYS.adminProjects, sanitizeAdminProjects(projects));
}

export function loadStoredContracts() {
  return sanitizeContracts(readStoredJSON<Contract[]>(STORAGE_KEYS.contracts, []));
}

export function saveStoredContracts(contracts: Contract[]) {
  writeStoredJSON(STORAGE_KEYS.contracts, sanitizeContracts(contracts));
}

export function loadStoredProjectCostsClean() {
  return sanitizeProjectCosts(readStoredJSON<ProjectCost[]>(STORAGE_KEYS.projectCosts, []));
}

export function saveStoredProjectCostsClean(costs: ProjectCost[]) {
  writeStoredJSON(STORAGE_KEYS.projectCosts, sanitizeProjectCosts(costs));
}

export function loadStoredTimesheetsClean() {
  return sanitizeTimesheets(readStoredJSON<MonthlyTimesheet[]>(STORAGE_KEYS.timesheets, []));
}

export function saveStoredTimesheetsClean(timesheets: MonthlyTimesheet[]) {
  writeStoredJSON(STORAGE_KEYS.timesheets, sanitizeTimesheets(timesheets));
}

export function sanitizeLegacyDemoBusinessDataInStorage() {
  if (typeof window === 'undefined') return false;

  const nextProjects = loadAdminProjects();
  const nextContracts = loadStoredContracts();
  const nextProjectCosts = loadStoredProjectCostsClean();
  const nextTimesheets = loadStoredTimesheetsClean();

  const writes: Array<[string, string]> = [
    [STORAGE_KEYS.adminProjects, JSON.stringify(nextProjects)],
    [STORAGE_KEYS.contracts, JSON.stringify(nextContracts)],
    [STORAGE_KEYS.projectCosts, JSON.stringify(nextProjectCosts)],
    [STORAGE_KEYS.timesheets, JSON.stringify(nextTimesheets)],
  ];

  let didChange = false;

  writes.forEach(([key, payload]) => {
    if (window.localStorage.getItem(key) !== payload) {
      window.localStorage.setItem(key, payload);
      didChange = true;
    }
  });

  return didChange;
}
